import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FormEvent } from 'react';
import { Badge, Button, Card, Checkbox, EmptyState, Field, HeroSelectField, Input, LoadingScreen, Modal, PageHeader, Table } from '../components/Ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  useBusinessBranches,
  useBusinessTables,
  useBusinessUsers,
  useBusinessWaiters,
  useCreateBusinessTable,
  useCreateBusinessUser,
  useCreateBusinessWaiter,
  useUpdateBusinessTable,
  useUpdateBusinessUser,
  useUpdateBusinessWaiter,
} from '../hooks/businessData';

interface BusinessUser { id: string; name: string; email: string; role: 'owner' | 'manager'; active: boolean; createdAt: string }
const initial = { name: '', email: '', role: 'manager' as 'owner' | 'manager', password: '', active: true };
const waiterInitial = { name: '', username: '', branchId: '', password: '', active: true };
const tableInitial = { name: '', zone: '', branchId: '', order: 0, active: true };

export default function UsersPage() {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { show } = useToast();
  const canManageDashboardUsers = currentUser?.role === 'owner';
  const { data: users = [], isLoading, isError, error } = useBusinessUsers(canManageDashboardUsers);
  const { data: branches = [] } = useBusinessBranches();
  const defaultBranchId = branches.find((branch) => branch.active)?.id ?? branches[0]?.id ?? '';
  const { data: waiters = [] } = useBusinessWaiters();
  const { data: tables = [] } = useBusinessTables(defaultBranchId);
  const createUserMutation = useCreateBusinessUser();
  const updateUserMutation = useUpdateBusinessUser();
  const createWaiterMutation = useCreateBusinessWaiter();
  const updateWaiterMutation = useUpdateBusinessWaiter();
  const createTableMutation = useCreateBusinessTable();
  const updateTableMutation = useUpdateBusinessTable();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [waiterForm, setWaiterForm] = useState(waiterInitial);
  const [tableForm, setTableForm] = useState(tableInitial);

  useEffect(() => {
    if (isError) {
      show(error instanceof Error ? error.message : 'No se pudieron cargar los usuarios.', 'error');
    }
  }, [error, isError, show]);
  const create = () => { setEditingId(null); setForm(initial); setOpen(true); };
  const edit = (member: BusinessUser) => { setEditingId(member.id); setForm({ name: member.name, email: member.email, role: member.role, password: '', active: member.active }); setOpen(true); };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingId && !form.password) return;
    setBusy(true);
    try {
      const payload = { ...form, password: form.password || undefined };
      if (editingId) {
        await updateUserMutation.mutateAsync({ id: editingId, payload });
      } else {
        await createUserMutation.mutateAsync(payload);
      }
      setOpen(false);
      if (editingId === currentUser?.userId && form.password) {
        show('Contraseña actualizada. Vuelve a iniciar sesión con la nueva contraseña.', 'success');
        await logout();
        navigate('/login', { replace: true });
        return;
      }
      show(editingId ? 'Usuario actualizado.' : 'Usuario creado.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo guardar el usuario.', 'error'); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (defaultBranchId) {
      setWaiterForm((current) => current.branchId ? current : { ...current, branchId: defaultBranchId });
      setTableForm((current) => current.branchId ? current : { ...current, branchId: defaultBranchId });
    }
  }, [defaultBranchId]);

  const submitWaiter = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await createWaiterMutation.mutateAsync(waiterForm);
      setWaiterForm({ ...waiterInitial, branchId: defaultBranchId });
      show('Mesero creado.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo guardar el mesero.', 'error'); }
  };

  const submitTable = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await createTableMutation.mutateAsync({ ...tableForm, zone: tableForm.zone || null });
      setTableForm({ ...tableInitial, branchId: defaultBranchId });
      show('Mesa creada.', 'success');
    } catch (error) { show(error instanceof Error ? error.message : 'No se pudo guardar la mesa.', 'error'); }
  };

  if (canManageDashboardUsers && isLoading) return <LoadingScreen />;
  return (
    <div>
      <PageHeader title="Usuarios" description="Administración de accesos operativos y mesas." action={canManageDashboardUsers ? <Button onClick={create}>+ Nuevo usuario</Button> : undefined} />
      {canManageDashboardUsers ? (users.length ? (
          <Table>
            <Table.ScrollContainer>
              <Table.Content aria-label="Usuarios" className="min-w-[650px]">
                <Table.Header>
                  <Table.Column isRowHeader>Nombre</Table.Column>
                  <Table.Column>Rol</Table.Column>
                  <Table.Column>Estado</Table.Column>
                  <Table.Column>Creado</Table.Column>
                  <Table.Column className="text-end">Acción</Table.Column>
                </Table.Header>
                <Table.Body>
                  {users.map((member) => (
                    <Table.Row key={member.id}>
                      <Table.Cell>
                        <div className="font-bold">{member.name}</div>
                        <div className="text-xs text-slate-500">{member.email}</div>
                      </Table.Cell>
                      <Table.Cell>{member.role === 'owner' ? 'Propietario' : 'Gerente'}</Table.Cell>
                      <Table.Cell>
                        <Badge tone={member.active ? 'green' : 'slate'}>{member.active ? 'activo' : 'inactivo'}</Badge>
                      </Table.Cell>
                      <Table.Cell>{new Date(member.createdAt).toLocaleDateString()}</Table.Cell>
                      <Table.Cell className="text-end">
                        <Button variant="ghost" onClick={() => edit(member)} disabled={member.id === currentUser?.userId && !member.active}>Editar</Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        ) : <EmptyState title="Sin usuarios" description="Agrega propietarios o gerentes." />) : null}
      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <Card>
          <h2 className="text-lg font-black">Meseros</h2>
          <p className="mt-1 text-sm text-slate-500">Accesos individuales por sucursal. No comparten PIN operativo.</p>
          <form onSubmit={submitWaiter} className="mt-4 grid gap-3">
            <HeroSelectField label="Sucursal" value={waiterForm.branchId || null} onChange={(value) => setWaiterForm({ ...waiterForm, branchId: String(value ?? '') })} options={branches.map((branch) => ({ id: branch.id, label: branch.name }))} />
            <Field label="Nombre"><Input required value={waiterForm.name} onChange={(e) => setWaiterForm({ ...waiterForm, name: e.target.value })} /></Field>
            <Field label="Usuario"><Input required value={waiterForm.username} onChange={(e) => setWaiterForm({ ...waiterForm, username: e.target.value })} /></Field>
            <Field label="Contraseña"><Input required type="password" minLength={8} value={waiterForm.password} onChange={(e) => setWaiterForm({ ...waiterForm, password: e.target.value })} /></Field>
            <Button type="submit" busy={createWaiterMutation.isPending}>Crear mesero</Button>
          </form>
          <div className="mt-5 grid gap-2">
            {waiters.map((waiter) => (
              <div key={waiter.id} className="flex items-center justify-between gap-3 rounded-xl border p-3 text-sm">
                <div><div className="font-bold">{waiter.name}</div><div className="text-xs text-slate-500">{waiter.username} · {waiter.branchName}</div></div>
                <Button variant="ghost" onClick={() => void updateWaiterMutation.mutateAsync({ id: waiter.id, payload: { branchId: waiter.branchId, name: waiter.name, username: waiter.username, active: !waiter.active } })}>{waiter.active ? 'Desactivar' : 'Activar'}</Button>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-black">Mesas</h2>
          <p className="mt-1 text-sm text-slate-500">Mesas disponibles para abrir cuentas desde Meseros.</p>
          <form onSubmit={submitTable} className="mt-4 grid gap-3">
            <HeroSelectField label="Sucursal" value={tableForm.branchId || null} onChange={(value) => setTableForm({ ...tableForm, branchId: String(value ?? '') })} options={branches.map((branch) => ({ id: branch.id, label: branch.name }))} />
            <Field label="Mesa"><Input required value={tableForm.name} onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })} /></Field>
            <Field label="Zona"><Input value={tableForm.zone} onChange={(e) => setTableForm({ ...tableForm, zone: e.target.value })} /></Field>
            <Button type="submit" busy={createTableMutation.isPending}>Crear mesa</Button>
          </form>
          <div className="mt-5 grid gap-2">
            {tables.map((table) => (
              <div key={table.id} className="flex items-center justify-between gap-3 rounded-xl border p-3 text-sm">
                <div><div className="font-bold">{table.name}</div><div className="text-xs text-slate-500">{table.zone || 'Sin zona'} {table.openAccountId ? '· cuenta abierta' : ''}</div></div>
                <Button variant="ghost" onClick={() => void updateTableMutation.mutateAsync({ id: table.id, payload: { branchId: table.branchId, name: table.name, zone: table.zone, order: table.order, active: !table.active } })}>{table.active ? 'Desactivar' : 'Activar'}</Button>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? 'Editar usuario' : 'Nuevo usuario'}>
        <form onSubmit={submit} className="grid gap-4"><Field label="Nombre"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field><Field label="Correo"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field><HeroSelectField label="Rol" value={form.role} onChange={(value) => setForm({ ...form, role: String(value ?? 'manager') as 'owner' | 'manager' })} options={[{ id: 'owner', label: 'Propietario' }, { id: 'manager', label: 'Gerente' }]} /><Field label={editingId ? 'Nueva contraseña (opcional)' : 'Contraseña'}><Input type="password" required={!editingId} minLength={10} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field><Checkbox label="Usuario activo" isSelected={form.active} isDisabled={editingId === currentUser?.userId} onChange={(active) => setForm({ ...form, active })} className="rounded-xl border p-3 text-sm font-semibold" /><div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" busy={busy}>Guardar</Button></div></form>
      </Modal>
    </div>
  );
}
